import { DecimalPipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { PageHeaderComponent } from '@shared/components/common/page-header/page-header.component';
import { SelectFieldComponent, SelectOption } from '@shared/components/form/select/select-field.component';
import { Badge } from '@shared/components/ui/badge/badge';
import { ErrorService } from '@shared/services/error.service';
import { etiquetaVaca, rpVaca } from '@shared/utils/etiqueta-vaca';
import { Cliente } from '../../crm/clientes/clientes.interface';
import { AgendamientosService } from '../../agendamientos/opu/agendamientos.service';
import { controlDeEtapa, totalProducido } from '../cultivos/cultivos-planilla';
import { agruparPor, NumerosFila, porcentaje, resumenHistorial, ResumenHistorial } from './pive-historial.model';
import { FilaHistorialApi, PiveHistorialService } from './pive-historial.service';

interface GrupoDonante {
  donanteId: number;
  etiqueta: string;
  raza: string;
  filas: FilaHistorialApi[];
}

/** Un OPU del cliente en el año y cuánto le falta cargar. */
interface OpuResumen {
  id: number;
  fecha: string;
  establecimiento: string | null;
  donantes: number;
  completas: number;
}

/** Un OPU con sus filas donante × toro, como un bloque de las hojas por año del Excel. */
interface GrupoOpu {
  id: number;
  fecha: string;
  establecimiento: string | null;
  filas: FilaHistorialApi[];
}

type VistaPive = 'donante' | 'opu';

const CLAVE_CLIENTE = 'pive.cliente';
const CLAVE_VISTA = 'pive.vista';

/**
 * PIVE del cliente, como la hoja "Historial de producción" del laboratorio:
 * agrupada por donante, una fila por OPU con viables, totales, cultivados,
 * clivados y embriones, y el TOTAL y PROMEDIO de cada donante.
 *
 * Es solo un resumen (2026-09-23): los datos se cargan en cada OPU, por pasos.
 * Desde acá se ve qué falta y se abre el OPU para cargarlo.
 */
@Component({
  selector: 'app-pive-historial',
  imports: [Badge, DecimalPipe, MatIcon, MatTooltip, PageHeaderComponent, ReactiveFormsModule, RouterLink, SelectFieldComponent],
  templateUrl: './pive-historial.html',
})
export class PiveHistorial implements OnInit {
  clientes: Cliente[] = [];
  anios: number[] = [];
  /** Todo lo que vino del cliente en el año; `filas` es lo que queda tras el filtro de establecimiento. */
  private todasLasFilas: FilaHistorialApi[] = [];
  establecimientos: string[] = [];
  filas: FilaHistorialApi[] = [];
  grupos: GrupoDonante[] = [];
  opus: OpuResumen[] = [];
  gruposOpu: GrupoOpu[] = [];
  loading = false;
  /** 'donante' = hoja "Historial de producción"; 'opu' = hojas por año, un bloque por OPU. */
  vista: VistaPive = 'donante';
  readonly claseVistaActiva = 'px-4 text-sm font-medium bg-brand-500 text-white';
  readonly claseVistaInactiva = 'px-4 text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03]';

  readonly filtros = new FormGroup({
    cliente: new FormControl<string>('', { nonNullable: true }),
    establecimiento: new FormControl<string>('', { nonNullable: true }),
    anio: new FormControl<string>('', { nonNullable: true }),
    buscar: new FormControl('', { nonNullable: true }),
  });

  private historialService = inject(PiveHistorialService);
  private agendamientosService = inject(AgendamientosService);
  private errorService = inject(ErrorService);

  get clientesOptions(): SelectOption[] {
    return this.clientes.map(c => ({ value: c.id, label: c.persona.nombre_generico }));
  }

  get establecimientosOptions(): SelectOption[] {
    return this.establecimientos.map(e => ({ value: e, label: e }));
  }

  get aniosOptions(): SelectOption[] {
    return this.anios.map(a => ({ value: a, label: String(a) }));
  }

  get gruposVisibles(): GrupoDonante[] {
    const texto = this.filtros.controls.buscar.value.trim().toLowerCase();
    return texto ? this.grupos.filter(g => g.etiqueta.toLowerCase().includes(texto)) : this.grupos;
  }

  get gruposOpuVisibles(): GrupoOpu[] {
    const texto = this.filtros.controls.buscar.value.trim().toLowerCase();
    if (!texto) return this.gruposOpu;
    return this.gruposOpu
      .map(grupo => ({ ...grupo, filas: grupo.filas.filter(f => this.donante(f).toLowerCase().includes(texto)) }))
      .filter(grupo => grupo.filas.length);
  }

  // Las sin aspirar se listan para ir a cargarlas, pero no cuentan como producción.
  get resumenCliente(): ResumenHistorial {
    return resumenHistorial(this.filas.filter(f => f.aspirado).map(f => this.numeros(f)));
  }

  ngOnInit(): void {
    try {
      const vista = localStorage.getItem(CLAVE_VISTA);
      if (vista === 'opu' || vista === 'donante') this.vista = vista;
    } catch { /* sin almacenamiento */ }

    this.agendamientosService.comboClientes().subscribe({
      next: (response) => {
        this.clientes = response.data;
        let ultimo = '';
        try { ultimo = localStorage.getItem(CLAVE_CLIENTE) ?? ''; } catch { /* sin almacenamiento */ }
        if (ultimo && this.clientes.some(c => String(c.id) === ultimo)) {
          this.filtros.controls.cliente.setValue(ultimo);
        }
      },
      error: (error) => this.errorService.handleError(error),
    });

    this.filtros.controls.cliente.valueChanges.subscribe(cliente => {
      try { localStorage.setItem(CLAVE_CLIENTE, cliente); } catch { /* sin almacenamiento */ }
      this.filtros.controls.anio.setValue('', { emitEvent: false });
      this.filtros.controls.establecimiento.setValue('', { emitEvent: false });
      this.cargar();
    });
    this.filtros.controls.anio.valueChanges.subscribe(() => this.cargar());
    this.filtros.controls.establecimiento.valueChanges.subscribe(() => this.armar());
  }

  cargar(): void {
    const clienteId = Number(this.filtros.controls.cliente.value);
    if (!clienteId) {
      this.todasLasFilas = [];
      this.establecimientos = [];
      this.filas = [];
      this.grupos = [];
      this.opus = [];
      this.gruposOpu = [];
      return;
    }

    this.loading = true;
    const anio = Number(this.filtros.controls.anio.value) || null;
    this.historialService.historial(clienteId, anio).subscribe({
      next: (response) => {
        const { filas, anios, anio: anioMostrado } = response.data;
        this.anios = anios;
        if (anioMostrado && !this.filtros.controls.anio.value) {
          this.filtros.controls.anio.setValue(String(anioMostrado), { emitEvent: false });
        }
        this.todasLasFilas = filas;
        this.establecimientos = [...new Set(filas.map(f => f.agendamiento.establecimiento).filter((e): e is string => !!e))]
          .sort((a, b) => a.localeCompare(b));
        // Si el establecimiento elegido no tiene OPU en el año nuevo, se vuelve a todos.
        if (!this.establecimientos.includes(this.filtros.controls.establecimiento.value)) {
          this.filtros.controls.establecimiento.setValue('', { emitEvent: false });
        }
        this.armar();
      },
      error: (error) => this.errorService.handleError(error),
    }).add(() => { this.loading = false; });
  }

  private armar(): void {
    const establecimiento = this.filtros.controls.establecimiento.value;
    const filas = establecimiento
      ? this.todasLasFilas.filter(f => f.agendamiento.establecimiento === establecimiento)
      : this.todasLasFilas;
    this.filas = filas;

    this.grupos = [...agruparPor(filas, f => f.ani_animal_donante_id).entries()]
      .map(([donanteId, delGrupo]) => ({
        donanteId,
        etiqueta: etiquetaVaca(delGrupo[0].animal_donante),
        raza: delGrupo[0].animal_donante.raza_nombre || delGrupo[0].animal_donante.raza_codigo || '',
        filas: delGrupo,
      }))
      .sort((a, b) => (Number(rpVaca(a.filas[0].animal_donante)) || 0) - (Number(rpVaca(b.filas[0].animal_donante)) || 0)
        || a.etiqueta.localeCompare(b.etiqueta));

    this.opus = [...agruparPor(filas, f => f.agendamiento.id).values()]
      .map(delOpu => ({
        id: delOpu[0].agendamiento.id,
        fecha: delOpu[0].agendamiento.fecha,
        establecimiento: delOpu[0].agendamiento.establecimiento,
        donantes: delOpu.length,
        completas: delOpu.filter(f => !this.faltante(f)).length,
      }))
      .sort((a, b) => b.fecha.localeCompare(a.fecha));

    // Como las hojas por año: OPU en orden de fecha y, dentro, donantes por RP.
    this.gruposOpu = [...agruparPor(filas, f => f.agendamiento.id).values()]
      .map(delOpu => ({
        id: delOpu[0].agendamiento.id,
        fecha: delOpu[0].agendamiento.fecha,
        establecimiento: delOpu[0].agendamiento.establecimiento,
        filas: [...delOpu].sort((a, b) =>
          (Number(rpVaca(a.animal_donante)) || 0) - (Number(rpVaca(b.animal_donante)) || 0)
          || this.donante(a).localeCompare(this.donante(b))
          || a.toro_fecundador.nombre.localeCompare(b.toro_fecundador.nombre)),
      }))
      .sort((a, b) => a.fecha.localeCompare(b.fecha));
  }

  cambiarVista(vista: VistaPive): void {
    this.vista = vista;
    try { localStorage.setItem(CLAVE_VISTA, vista); } catch { /* sin almacenamiento */ }
  }

  donante(fila: FilaHistorialApi): string {
    return etiquetaVaca(fila.animal_donante);
  }

  raza(fila: FilaHistorialApi): string {
    return fila.animal_donante.raza_nombre || fila.animal_donante.raza_codigo || '';
  }

  /** TOTAL y PROMEDIO del bloque; el Excel cuenta donantes (no filas) y embriones por donante. */
  resumenOpu(grupo: GrupoOpu): ResumenHistorial & { donantes: number; embrionesPorDonante: number | null } {
    const r = resumenHistorial(grupo.filas.filter(f => f.aspirado).map(f => this.numeros(f)));
    const donantes = new Set(grupo.filas.map(f => f.ani_animal_donante_id)).size;
    return { ...r, donantes, embrionesPorDonante: donantes ? r.embriones / donantes : null };
  }

  numeros(fila: FilaHistorialApi): NumerosFila {
    const cultivo = fila.cultivo;
    return {
      viables: fila.viables,
      totales: fila.total_lab,
      cultivados: cultivo ? controlDeEtapa(cultivo, 1)?.cantidad ?? null : null,
      clivados: cultivo ? controlDeEtapa(cultivo, 4)?.cantidad ?? null : null,
      embriones: cultivo ? totalProducido(cultivo) : null,
    };
  }

  /** Embriones por destino, para el detalle "10 vitrif. · 2 frescos". */
  porDestino(fila: FilaHistorialApi, destino: 'F' | 'V'): number {
    return (fila.cultivo?.salidas_cultivo ?? [])
      .filter(s => s.destino === destino)
      .reduce((total, s) => total + s.nro_embriones, 0);
  }

  produccion(fila: FilaHistorialApi): number | null {
    const n = this.numeros(fila);
    return porcentaje(n.embriones, n.cultivados);
  }

  resumen(grupo: GrupoDonante): ResumenHistorial {
    return resumenHistorial(grupo.filas.filter(f => f.aspirado).map(f => this.numeros(f)));
  }

  /** Qué le falta cargar a la fila, en el orden del proceso; null si está completa. */
  faltante(fila: FilaHistorialApi): string | null {
    if (!fila.aspirado) return 'Falta la aspiración';
    const recepcion = fila.hora_llegada && fila.temperatura !== null && fila.temperatura !== undefined && fila.hora_inicio_miv;
    if (!recepcion) return 'Falta la recepción';
    if (!fila.fiv) return 'Falta la FIV';
    const n = this.numeros(fila);
    if (n.cultivados === null || n.clivados === null) return 'Falta el cultivo';
    if (n.embriones === null) return 'Faltan los embriones';
    return null;
  }

  fecha(iso: string): string {
    return iso.split('-').reverse().join('/');
  }
}
